<template>
	<el-dialog :visible="visible" width="840px" top="6vh" custom-class="social-post-dialog"
		:show-close="true" append-to-body @close="onClose">
		<!-- 头部: 品牌 + 平台选择 -->
		<div slot="title" class="sp-header">
			<div class="sp-brand">
				<span class="sp-logo">一键发帖</span>
				<span class="sp-slogan">绑定你自己的平台账号，发布属于你的内容 · 绑一次长期免登录</span>
			</div>
			<div class="sp-platforms">
				<div v-for="p in platforms" :key="p.platform" class="sp-plat"
					:class="{active: curPlatform === p.platform, disabled: !p.enabled}"
					@click="selectPlatform(p)">
					<span class="sp-plat-label">{{ p.label }}</span>
					<span v-if="p.bound" class="sp-dot" title="已绑定"></span>
					<span v-if="!p.enabled" class="sp-soon">即将支持</span>
				</div>
				<div v-if="!platforms.length" class="sp-plat-empty">平台加载中…</div>
			</div>
		</div>

		<div v-loading="loading" class="sp-body">
			<!-- 未开通平台 -->
			<div v-if="curPlatformObj && !curPlatformObj.enabled" class="sp-disabled">
				<i class="el-icon-lock"></i>
				<b>{{ curPlatformObj.label }}</b> 暂未开通：{{ curPlatformObj.reason || '需接入平台签名/接口后开放' }}
				<div class="sp-disabled-tip">该平台需要服务端链上签名能力，正在排期接入；已开通的平台可正常发帖。</div>
			</div>

			<el-tabs v-else v-model="tab" class="sp-tabs" @tab-click="onTabClick">
				<!-- ============ 绑定账号 ============ -->
				<el-tab-pane label="绑定账号" name="bind">
					<!-- 已绑定 -->
					<div v-if="curPlatformObj.bound" class="sp-bound-card">
						<div class="sp-bound-avatar"><i class="el-icon-success"></i></div>
						<div class="sp-bound-info">
							<div class="sp-bound-name">{{ curPlatformObj.displayName || '已绑定账号' }}</div>
							<div class="sp-bound-id" v-if="curPlatformObj.accountId">账号ID：{{ curPlatformObj.accountId }}</div>
							<div class="sp-bound-ok">已绑定，可直接去「发布帖子」发帖，无需再次登录</div>
						</div>
						<div class="sp-bound-ops">
							<el-button size="mini" @click="doUnbind">解绑</el-button>
							<el-button size="mini" type="primary" plain @click="goPublish">去发帖</el-button>
						</div>
					</div>

					<!-- 待授权(扫码) -->
					<div v-else-if="approvalUrl" class="sp-approve">
						<div class="sp-approve-title">用 {{ curPlatformObj.label }} 官方App扫码授权</div>
						<img v-if="approvalQr" :src="approvalQr" class="sp-qr" alt="授权二维码" />
						<div class="sp-approve-link">
							手机不方便扫码？直接
							<a :href="approvalUrl" target="_blank" rel="noopener">点此打开授权页</a>
						</div>
						<div class="sp-approve-tip">
							{{ pollTip }}<br />
							<span class="sp-muted">授权由平台官方完成，员工0付费；凭证加密保存在本系统，之后长期免登录。</span>
						</div>
						<el-button size="small" type="primary" :loading="binding" @click="refreshBindStatus">
							我已完成授权，刷新状态
						</el-button>
						<el-button size="small" @click="cancelBind">取消</el-button>
					</div>

					<!-- 未绑定 -->
					<div v-else class="sp-bind-intro">
						<div class="sp-step"><span class="sp-step-no">1</span> 先在
							<b>{{ curPlatformObj.label }}</b> 官方注册一个属于你自己的账号（IM账号无法登录发帖平台）<a v-if="curRegisterUrl" class="sp-reg-link" :href="curRegisterUrl" target="_blank" rel="noopener">点此去官方注册 →</a>。</div>
						<div class="sp-step"><span class="sp-step-no">2</span> 点下方按钮发起绑定，用官方App扫码/点链接授权一次。</div>
						<div class="sp-step"><span class="sp-step-no">3</span> 授权成功后账号会被记住，之后发帖/看数据/回评论都无需再登录。</div>
						<div class="sp-bind-input" v-if="needCredentialInput">
							<el-input v-model="bindInput" type="textarea" :rows="2"
								placeholder="粘贴你的平台凭证（私钥/授权码，仅用于本系统发帖，加密保存）"></el-input>
						</div>
						<el-button type="primary" :loading="binding" @click="doBind">
							绑定我的 {{ curPlatformObj.label }} 账号
						</el-button>
					</div>
				</el-tab-pane>

				<!-- ============ 发布帖子 ============ -->
				<el-tab-pane label="发布帖子" name="publish">
					<div v-if="!curPlatformObj.bound" class="sp-need-bind">
						<i class="el-icon-warning-outline"></i> 请先在「绑定账号」完成 {{ curPlatformObj.label }} 账号绑定，再回来发帖。
						<el-button size="mini" type="text" @click="tab = 'bind'">去绑定</el-button>
					</div>
					<div v-else class="sp-publish">
						<el-input v-model="postText" type="textarea" :rows="5" maxlength="320" show-word-limit
							placeholder="写点什么…（以你自己绑定的账号身份发布）"></el-input>
						<div class="sp-pub-row">
							<el-input v-model="postChannel" size="small" placeholder="频道/社区（可空，如 farcaster 的 channel）"
								class="sp-pub-channel"></el-input>
						</div>
						<div class="sp-pub-row sp-pub-imgs">
							<input ref="imgPicker" type="file" accept="image/*" multiple style="display:none" @change="onPickImages" />
							<el-button size="small" :loading="uploadingImgs" @click="$refs.imgPicker.click()">
								<i class="el-icon-picture-outline"></i> 上传配图(≤4张)
							</el-button>
							<div v-for="(im, i) in postImages" :key="i" class="sp-pub-img">
								<img :src="im.url" alt="配图" />
								<i class="el-icon-close sp-pub-img-del" @click="postImages.splice(i, 1)"></i>
							</div>
						</div>
						<div class="sp-pub-ops">
							<el-button size="mini" plain @click="copyAndOpenGate">
								<i class="el-icon-link"></i> Gate Square 网页发帖(复制文案并打开)
							</el-button>
							<el-button type="primary" :loading="publishing" @click="doPublish">
								<i class="el-icon-s-promotion"></i> 发布
							</el-button>
							<span class="sp-muted">发布身份：{{ curPlatformObj.displayName || curPlatformObj.accountId }}</span>
						</div>
						<div v-if="lastPublish" class="sp-pub-result">
							<i class="el-icon-circle-check"></i> 发布成功！
							<a v-if="lastPublish.postUrl" :href="lastPublish.postUrl" target="_blank" rel="noopener">查看帖子</a>
						</div>
					</div>
				</el-tab-pane>

				<!-- ============ 我的发帖(数据) ============ -->
				<el-tab-pane label="我的发帖" name="posts">
					<div class="sp-list-bar">
						<el-checkbox v-model="postsScopeAll" @change="loadPosts">查看全部平台</el-checkbox>
						<el-button size="mini" icon="el-icon-refresh" @click="loadPosts">刷新列表</el-button>
					</div>
					<div v-if="!posts.length" class="sp-empty">还没有发帖记录，去「发布帖子」发第一条吧</div>
					<div v-for="p in posts" :key="p.id" class="sp-post">
						<div class="sp-post-head">
							<span class="sp-tag">{{ platLabel(p.platform) }}</span>
							<span class="sp-time">{{ fmtTime(p.publishedTime) }}</span>
							<a v-if="p.postUrl" :href="p.postUrl" target="_blank" rel="noopener" class="sp-link">原帖</a>
						</div>
						<div class="sp-post-text">{{ p.text }}</div>
						<div v-if="p.images && p.images.length" class="sp-my-imgs">
							<img v-for="(u, i) in p.images" :key="i" :src="u" alt="配图" @click="openImg(u)" />
						</div>
						<!-- 币安未开放读取API, 拿不到真实互动数据, 整行不显示(不造假不手录) -->
						<div v-if="p.platform !== 'binance'" class="sp-post-metrics">
							<span><i class="el-icon-star-off"></i> 赞 {{ p.likes || 0 }}</span>
							<span><i class="el-icon-refresh"></i> 转 {{ p.reposts || 0 }}</span>
							<span><i class="el-icon-chat-dot-round"></i> 评 {{ p.replies || 0 }}</span>
							<span><i class="el-icon-view"></i> 浏览 {{ p.views == null ? '—' : p.views }}</span>
							<el-button size="mini" type="text" :loading="p._refreshing"
								@click="refreshPost(p)">更新数据</el-button>
						</div>
					</div>
				</el-tab-pane>

				<!-- ============ 评论收件箱 ============ -->
				<el-tab-pane label="评论收件箱" name="comments">
					<div class="sp-list-bar">
						<el-checkbox v-model="onlyUnreplied" @change="loadComments">只看未回复</el-checkbox>
						<el-checkbox v-model="commentsScopeAll" @change="loadComments">查看全部平台</el-checkbox>
						<el-button size="mini" icon="el-icon-download" :loading="pulling" @click="pullComments">拉取最新评论</el-button>
					</div>
					<div v-if="!comments.length" class="sp-empty">暂无评论。别人回复/提及你的帖子后会实时进这里（也可点「拉取最新评论」）</div>
					<div v-for="c in comments" :key="c.id" class="sp-comment" :class="{replied: c.replied}">
						<div class="sp-comment-head">
							<span class="sp-tag">{{ platLabel(c.platform) }}</span>
							<b class="sp-comment-from">{{ c.fromName || c.fromAccount || '匿名' }}</b>
							<span class="sp-muted" v-if="c.fromAccount">@{{ c.fromAccount }}</span>
							<span class="sp-time">{{ fmtTime(c.createdTime) }}</span>
							<span v-if="c.replied" class="sp-replied-tag">已回复</span>
						</div>
						<div class="sp-comment-text">{{ c.text }}</div>
						<div class="sp-comment-reply" v-if="!c.replied">
							<el-input v-model="replyMap[c.id]" size="small" placeholder="写下你的回复…"
								@keyup.enter.native="replyTo(c)"></el-input>
							<el-button size="mini" type="primary" :loading="replyingId === c.id"
								@click="replyTo(c)">回复</el-button>
						</div>
					</div>
				</el-tab-pane>
			</el-tabs>
		</div>

	</el-dialog>
</template>

<script>
import QRCode from 'qrcode';
import socialPostApi from '../../api/socialPostApi.js';

export default {
	name: "chatPostDialog",
	props: {
		visible: Boolean
	},
	data() {
		return {
			loading: false,
			platforms: [],
			curPlatform: '',
			tab: 'bind',
			// 绑定
			binding: false,
			approvalUrl: '',
			approvalQr: '',
			bindInput: '',
			needCredentialInput: false,
			pollTimer: null,
			pollCount: 0,
			pollTip: '等待你在手机App中确认授权…',
			// 发布
			publishing: false,
			postText: '',
			postChannel: '',
			postImages: [],
			uploadingImgs: false,
			lastPublish: null,
			// 我的发帖
			posts: [],
			postsScopeAll: true,
			// 评论
			comments: [],
			onlyUnreplied: false,
			commentsScopeAll: false,
			replyMap: {},
			replyingId: null,
			pulling: false
		};
	},
	computed: {
		curPlatformObj() {
			return this.platforms.find((p) => p.platform === this.curPlatform) || {};
		},
		curRegisterUrl() {
			const map = {
				farcaster: 'https://warpcast.com/',
				nostr: 'https://primal.net/',
				lens: 'https://www.lens.xyz/',
				hive: 'https://signup.hive.io/',
				deso: 'https://www.deso.com/',
				binance: 'https://www.binance.com/square/creator-center/home'
			};
			return map[this.curPlatform] || '';
		}
	},
	watch: {
		visible(v) {
			if (v) {
				this.loadPlatforms();
			} else {
				this.stopPoll();
			}
		}
	},
	beforeDestroy() {
		this.stopPoll();
	},
	methods: {
		onClose() {
			this.stopPoll();
			this.$emit('close');
		},
		async loadPlatforms() {
			this.loading = true;
			try {
				const list = await socialPostApi.platforms();
				this.platforms = list || [];
				// 默认选中: 当前选中的仍在则保留, 否则选第一个已开通的, 再否则第一个
				const still = this.platforms.find((p) => p.platform === this.curPlatform);
				if (!still) {
					const firstEnabled = this.platforms.find((p) => p.enabled);
					this.curPlatform = firstEnabled ? firstEnabled.platform
						: (this.platforms[0] ? this.platforms[0].platform : '');
				}
				this.syncPlatformState();
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.loading = false;
			}
		},
		selectPlatform(p) {
			if (this.curPlatform === p.platform) return;
			this.stopPoll();
			this.curPlatform = p.platform;
			this.syncPlatformState();
		},
		// 切换平台后重置绑定/发布态, 并按需加载当前页签数据
		syncPlatformState() {
			this.approvalUrl = '';
			this.approvalQr = '';
			this.bindInput = '';
			this.lastPublish = null;
			this.pollCount = 0;
			this.pollTip = '等待你在手机App中确认授权…';
			// 授权型平台(Farcaster)无需粘贴凭证; 凭证型平台(币安/Nostr/Hive/DeSo/Lens)需粘贴 key/私钥
			this.needCredentialInput = ['binance', 'nostr', 'hive', 'deso', 'lens'].includes(this.curPlatform);
			if (this.tab === 'posts') this.loadPosts();
			else if (this.tab === 'comments') this.loadComments();
		},
		onTabClick(t) {
			const name = t.name;
			if (name === 'posts') this.loadPosts();
			else if (name === 'comments') this.loadComments();
		},
		goPublish() {
			this.tab = 'publish';
		},
		platLabel(code) {
			const p = this.platforms.find((x) => x.platform === code);
			return p ? p.label : code;
		},

		// ---------- 绑定 ----------
		async doBind() {
			if (this.needCredentialInput && !this.bindInput) {
				this.$message.warning('请先粘贴平台凭证');
				return;
			}
			this.binding = true;
			try {
				const r = await socialPostApi.bind(this.curPlatform, this.bindInput);
				if (r && r.approvalUrl) {
					this.approvalUrl = r.approvalUrl;
					await this.genQr(r.approvalUrl);
					this.startPoll();
				} else if (r && r.status === 1) {
					this.$message.success(r.message || '绑定成功');
					await this.loadPlatforms();
					this.tab = 'publish';
				} else {
					this.$message.success((r && r.message) || '已发起绑定');
					await this.loadPlatforms();
				}
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.binding = false;
			}
		},
		async genQr(url) {
			try {
				this.approvalQr = await QRCode.toDataURL(url, {
					width: 220, margin: 1, color: { dark: '#000000', light: '#ffffff' }
				});
			} catch (e) {
				this.approvalQr = '';
			}
		},
		async refreshBindStatus() {
			this.binding = true;
			try {
				const r = await socialPostApi.refreshBind(this.curPlatform);
				if (r && r.status === 1) {
					this.stopPoll();
					this.approvalUrl = '';
					this.approvalQr = '';
					this.$message.success(r.message || '授权成功，账号已记住');
					await this.loadPlatforms();
					this.tab = 'publish';
				} else if (r && r.status === 2) {
					this.stopPoll();
					this.$message.error(r.message || '授权已失效，请重新绑定');
					await this.loadPlatforms();
				} else {
					this.pollTip = (r && r.message) || '还没检测到授权，请在手机App中点确认…';
				}
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.binding = false;
			}
		},
		startPoll() {
			this.stopPoll();
			this.pollCount = 0;
			this.pollTimer = setInterval(() => {
				this.pollCount++;
				if (this.pollCount > 45) { // ~3分钟
					this.stopPoll();
					this.pollTip = '等待超时，可点下方按钮手动刷新';
					return;
				}
				this.refreshBindStatus();
			}, 4000);
		},
		stopPoll() {
			if (this.pollTimer) {
				clearInterval(this.pollTimer);
				this.pollTimer = null;
			}
		},
		cancelBind() {
			this.stopPoll();
			this.approvalUrl = '';
			this.approvalQr = '';
		},
		async doUnbind() {
			try {
				await this.$confirm('解绑后需要重新扫码授权才能发帖，确定解绑吗？', '提示', { type: 'warning' });
			} catch (e) {
				return;
			}
			try {
				await socialPostApi.unbind(this.curPlatform);
				this.$message.success('已解绑');
				await this.loadPlatforms();
			} catch (e) {
				// 拦截器已提示
			}
		},

		openImg(u) {
			window.open(u, '_blank', 'noopener');
		},

		// ---------- Gate Square 网页发帖助手(Gate 无开放发帖API, 复制文案+打开官方网页人工发布) ----------
		async copyAndOpenGate() {
			const text = (this.postText || '').trim();
			if (!text) {
				this.$message.warning('请先输入发帖内容');
				return;
			}
			try {
				await navigator.clipboard.writeText(text);
			} catch (e) {
				const ta = document.createElement('textarea');
				ta.value = text;
				document.body.appendChild(ta);
				ta.select();
				document.execCommand('copy');
				document.body.removeChild(ta);
			}
			window.open('https://www.gate.com/zh/post', '_blank', 'noopener');
			this.$message.success('文案已复制，请在打开的 Gate 页面粘贴后发布');
		},

		// ---------- 配图上传(电脑直传, 先存本系统 /image/upload, 发布时转传币安) ----------
		async onPickImages(e) {
			const files = Array.from(e.target.files || []);
			e.target.value = '';
			if (!files.length) return;
			if (this.postImages.length + files.length > 4) {
				this.$message.warning('币安广场最多 4 张配图');
				return;
			}
			this.uploadingImgs = true;
			try {
				for (const f of files) {
					const fd = new FormData();
					fd.append('file', f);
					const d = await this.$http.post('/image/upload?isPermanent=true', fd, {
						headers: { 'Content-Type': 'multipart/form-data' }
					});
					this.postImages.push({ url: d.originUrl || d.thumbUrl, name: f.name });
				}
			} catch (err) {
				// 拦截器已提示
			} finally {
				this.uploadingImgs = false;
			}
		},

		// ---------- 发布 ----------
		async doPublish() {
			const text = (this.postText || '').trim();
			if (!text) {
				this.$message.warning('请输入发帖内容');
				return;
			}
			const embedUrls = this.postImages.map((im) => im.url);
			this.publishing = true;
			try {
				const r = await socialPostApi.publish({
					platform: this.curPlatform,
					text: text,
					channel: this.postChannel || null,
					parentExternalId: null,
					embedUrls: embedUrls
				});
				this.lastPublish = r;
				this.postText = '';
				this.postImages = [];
				this.$message.success('发布成功');
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.publishing = false;
			}
		},

		// ---------- 我的发帖 ----------
		async loadPosts() {
			this.loading = true;
			try {
				const platform = this.postsScopeAll ? null : this.curPlatform;
				const list = await socialPostApi.myPosts(platform, 50);
				this.posts = (list || []).map((p) => Object.assign({ _refreshing: false }, p));
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.loading = false;
			}
		},
		async refreshPost(p) {
			this.$set(p, '_refreshing', true);
			try {
				const r = await socialPostApi.refreshMetrics(p.id);
				if (r) {
					p.likes = r.likes;
					p.reposts = r.reposts;
					p.replies = r.replies;
					p.views = r.views;
				}
				this.$message.success('数据已更新');
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.$set(p, '_refreshing', false);
			}
		},
		// ---------- 评论 ----------
		async loadComments() {
			this.loading = true;
			try {
				const platform = this.commentsScopeAll ? null : this.curPlatform;
				const list = await socialPostApi.myComments(platform, this.onlyUnreplied, 50);
				this.comments = list || [];
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.loading = false;
			}
		},
		async pullComments() {
			if (!this.curPlatformObj.bound) {
				this.$message.warning('请先绑定该平台账号');
				return;
			}
			this.pulling = true;
			try {
				const added = await socialPostApi.pullComments(this.curPlatform);
				this.$message.success(added > 0 ? ('新增 ' + added + ' 条评论') : '暂无新评论');
				await this.loadComments();
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.pulling = false;
			}
		},
		async replyTo(c) {
			const text = (this.replyMap[c.id] || '').trim();
			if (!text) {
				this.$message.warning('请输入回复内容');
				return;
			}
			this.replyingId = c.id;
			try {
				await socialPostApi.replyComment(c.id, text);
				this.$message.success('回复成功');
				this.$set(this.replyMap, c.id, '');
				await this.loadComments();
			} catch (e) {
				// 拦截器已提示
			} finally {
				this.replyingId = null;
			}
		},

		fmtTime(t) {
			if (!t) return '';
			const s = String(t).replace('T', ' ');
			const m = s.match(/(\d{2,4})-(\d{2})-(\d{2})[ T]?(\d{2}):(\d{2})/);
			if (!m) return s.slice(5, 16);
			return `${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
		}
	}
};
</script>

<style lang="scss">
.social-post-dialog {
	border-radius: 10px;
	overflow: hidden;

	.el-dialog__header {
		padding: 0;
		border-bottom: none;
	}

	.el-dialog__headerbtn {
		top: 16px;
		right: 16px;
		z-index: 2;

		i {
			color: #fff;
		}
	}

	.el-dialog__body {
		padding: 0;
	}
}

.sp-header {
	background: linear-gradient(135deg, #6a3bd6, #2d8cf0);
	padding: 14px 20px 0;
	color: #fff;

	.sp-brand {
		display: flex;
		align-items: baseline;
		gap: 10px;
		flex-wrap: wrap;

		.sp-logo {
			font-size: 20px;
			font-weight: 700;
			letter-spacing: 1px;
		}

		.sp-slogan {
			font-size: 12px;
			opacity: .8;
		}
	}

	.sp-platforms {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 12px;

		.sp-plat {
			position: relative;
			padding: 7px 16px;
			font-size: 13px;
			cursor: pointer;
			border-radius: 6px 6px 0 0;
			opacity: .85;
			display: flex;
			align-items: center;
			gap: 6px;

			&.active {
				background: #fff;
				color: #6a3bd6;
				font-weight: 700;
				opacity: 1;
			}

			&.disabled {
				opacity: .55;
			}

			.sp-dot {
				width: 7px;
				height: 7px;
				border-radius: 50%;
				background: #22c55e;
				display: inline-block;
			}

			.sp-soon {
				font-size: 10px;
				background: rgba(255, 255, 255, .25);
				border-radius: 3px;
				padding: 0 4px;
			}

			&.active .sp-soon {
				background: #eee;
				color: #999;
			}
		}

		.sp-plat-empty {
			font-size: 13px;
			opacity: .8;
			padding: 7px 0;
		}
	}
}

.sp-body {
	min-height: 56vh;
	max-height: 66vh;
	overflow-y: auto;
	background: #fff;
	padding: 6px 18px 18px;
}

.sp-disabled {
	text-align: center;
	color: #66707f;
	padding: 70px 30px;
	font-size: 14px;

	i {
		font-size: 40px;
		color: #c9d2e0;
		display: block;
		margin-bottom: 14px;
	}

	b {
		color: #2b3340;
	}

	.sp-disabled-tip {
		margin-top: 10px;
		font-size: 12px;
		color: #98a2b3;
	}
}

.sp-tabs {
	.el-tabs__item.is-active {
		color: #6a3bd6;
	}

	.el-tabs__active-bar {
		background-color: #6a3bd6;
	}
}

.sp-muted {
	color: #98a2b3;
	font-size: 12px;
}

/* ---------- 绑定 ---------- */
.sp-my-imgs {
	margin: 6px 0;
}

.sp-my-imgs img {
	width: 72px;
	height: 72px;
	object-fit: cover;
	border-radius: 6px;
	border: 1px solid #e3e0f2;
	margin-right: 6px;
	cursor: pointer;
}

.sp-pub-imgs {
	align-items: center;
}

.sp-pub-img {
	position: relative;
	display: inline-block;
	margin-left: 6px;
}

.sp-pub-img img {
	width: 56px;
	height: 56px;
	object-fit: cover;
	border-radius: 6px;
	border: 1px solid #e3e0f2;
}

.sp-pub-img-del {
	position: absolute;
	top: -6px;
	right: -6px;
	background: #fff;
	border-radius: 50%;
	cursor: pointer;
	color: #909399;
}

.sp-reg-link {
	color: #6a3bd6;
	font-weight: 600;
	text-decoration: underline;
	margin-left: 4px;
}

.sp-bind-intro {
	padding: 10px 4px;

	.sp-step {
		font-size: 14px;
		color: #3a4353;
		line-height: 2.1;

		.sp-step-no {
			display: inline-block;
			width: 20px;
			height: 20px;
			line-height: 20px;
			text-align: center;
			border-radius: 50%;
			background: #6a3bd6;
			color: #fff;
			font-size: 12px;
			margin-right: 8px;
		}
	}

	.sp-bind-input {
		margin: 14px 0;
	}

	.el-button {
		margin-top: 12px;
	}
}

.sp-approve {
	text-align: center;
	padding: 10px 0 20px;

	.sp-approve-title {
		font-size: 15px;
		font-weight: 600;
		color: #2b3340;
		margin-bottom: 12px;
	}

	.sp-qr {
		width: 220px;
		height: 220px;
		border: 1px solid #eee;
		border-radius: 8px;
	}

	.sp-approve-link {
		margin-top: 10px;
		font-size: 13px;
		color: #66707f;

		a {
			color: #2d8cf0;
		}
	}

	.sp-approve-tip {
		margin: 12px auto;
		max-width: 460px;
		font-size: 13px;
		color: #6a3bd6;
		line-height: 1.7;
	}

	.el-button {
		margin: 6px 6px 0;
	}
}

.sp-bound-card {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 22px 16px;
	background: #f6fffb;
	border: 1px solid #d6f5e3;
	border-radius: 10px;
	margin-top: 10px;

	.sp-bound-avatar i {
		font-size: 40px;
		color: #22c55e;
	}

	.sp-bound-info {
		flex: 1;

		.sp-bound-name {
			font-size: 16px;
			font-weight: 700;
			color: #2b3340;
		}

		.sp-bound-id {
			font-size: 12px;
			color: #66707f;
			margin-top: 3px;
		}

		.sp-bound-ok {
			font-size: 12px;
			color: #22c55e;
			margin-top: 5px;
		}
	}
}

/* ---------- 发布 ---------- */
.sp-need-bind {
	text-align: center;
	color: #f5a623;
	padding: 60px 20px;
	font-size: 14px;

	i {
		font-size: 34px;
		display: block;
		margin-bottom: 12px;
	}
}

.sp-publish {
	padding: 8px 2px;

	.sp-pub-row {
		display: flex;
		gap: 10px;
		margin-top: 10px;

		.sp-pub-channel {
			flex: 1;
		}

		.sp-pub-embed {
			flex: 1;
		}
	}

	.sp-pub-ops {
		margin-top: 14px;
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.sp-pub-result {
		margin-top: 12px;
		color: #22c55e;
		font-size: 13px;

		a {
			color: #2d8cf0;
			margin-left: 6px;
		}
	}
}

/* ---------- 列表通用 ---------- */
.sp-list-bar {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 4px 0 12px;
	border-bottom: 1px solid #f0f2f5;
	margin-bottom: 8px;

	.el-button {
		margin-left: auto;
	}
}

.sp-empty {
	text-align: center;
	color: #98a2b3;
	padding: 50px 0;
	font-size: 13px;
}

.sp-tag {
	display: inline-block;
	background: #f0ebff;
	color: #6a3bd6;
	border-radius: 3px;
	font-size: 11px;
	padding: 1px 7px;
	margin-right: 8px;
}

.sp-time {
	color: #98a2b3;
	font-size: 12px;
}

.sp-link {
	color: #2d8cf0;
	font-size: 12px;
	margin-left: 8px;
	text-decoration: none;
}

.sp-post {
	padding: 12px 6px;
	border-bottom: 1px dashed #eee;

	.sp-post-head {
		display: flex;
		align-items: center;
	}

	.sp-post-text {
		font-size: 14px;
		color: #2b3340;
		line-height: 1.7;
		margin: 6px 0;
		word-break: break-all;
		white-space: pre-wrap;
	}

	.sp-post-metrics {
		display: flex;
		align-items: center;
		gap: 18px;
		font-size: 12px;
		color: #66707f;

		.el-button {
			margin-left: auto;
		}
	}
}

.sp-comment {
	padding: 12px 6px;
	border-bottom: 1px dashed #eee;

	&.replied {
		opacity: .7;
	}

	.sp-comment-head {
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;

		.sp-comment-from {
			font-size: 13px;
			color: #2b3340;
		}

		.sp-replied-tag {
			background: #e8f8ef;
			color: #22c55e;
			font-size: 11px;
			border-radius: 3px;
			padding: 1px 6px;
		}
	}

	.sp-comment-text {
		font-size: 14px;
		color: #3a4353;
		line-height: 1.7;
		margin: 6px 0;
		word-break: break-all;
	}

	.sp-comment-reply {
		display: flex;
		gap: 8px;

		.el-input {
			flex: 1;
		}
	}
}
</style>
